import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  PhoneCall,
  BarChart3,
  DollarSign,
  CheckCircle,
  Gauge,
  TrendingUp,
} from "lucide-react";
import { BASE_URL } from "../config";

/* =====================================================
   UTILS
===================================================== */
const addHoursUTC = (isoString, hours) => {
  if (!isoString) return "";
  const date = new Date(isoString.endsWith("Z") ? isoString : isoString + "Z");
  date.setUTCHours(date.getUTCHours() + hours);
  return date.toISOString();
};

/* =====================================================
   MAIN COMPONENT
===================================================== */
export default function AgentPerformanceDashboard() {
  const [data, setData] = useState(null);

  const getDates = () => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return {
      startAdjusted: addHoursUTC(start.toISOString(), 5),
      endAdjusted: addHoursUTC(now.toISOString(), 5),
    };
  };

  useEffect(() => {
    const { startAdjusted, endAdjusted } = getDates();

    fetch(
      `${BASE_URL}get_agent_stats.php?startDate=${encodeURIComponent(
        startAdjusted
      )}&endDate=${encodeURIComponent(endAdjusted)}`,
      { credentials: "include" }
    )
      .then((res) => res.json())
      .then((json) => setData(json.data));
  }, []);

  if (!data) return null;

  const {
    agent_name,
    marketing_spent,
    total_calls,
    MCO,
    bookings,
    conversion,
    revenue_ratio,
    rank,
  } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full rounded-2xl border border-white/10 bg-[#0f172a]/60 backdrop-blur-2xl shadow-2xl p-8 mb-6"
    >
      <h2 className="text-white font-semibold mb-8 text-lg flex items-center gap-3">
        <BarChart3 size={22} className="text-cyan-400" />
        {agent_name}'s KPI Overview
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

        <KPI
          label="Marketing Spend"
          value={`$${marketing_spent}`}
          icon={DollarSign}
          glow="emerald"
        />

        <KPI
          label="Total Calls"
          value={total_calls}
          icon={PhoneCall}
          glow="rose"
        />

        <KPI
          label="Bookings"
          value={bookings}
          icon={CheckCircle}
          glow="cyan"
        />

        <KPI
          label="Total MCO"
          value={`$${MCO}`}
          icon={Gauge}
          glow="blue"
        />

        <KPI
          label="Conversion"
          value={`${conversion}%`}
          icon={TrendingUp}
          glow="orange"
        />

        <KPI
          label="Revenue Ratio"
          value={revenue_ratio}
          icon={BarChart3}
          glow="purple"
        />

        {rank && (
          <KPI
            label="Rank"
            value={`#${rank}`}
            icon={CheckCircle}
            glow="amber"
          />
        )}
      </div>
    </motion.div>
  );
}

/* =====================================================
   PREMIUM GLOW CARD
===================================================== */
const KPI = ({ label, value, icon: Icon, glow = "cyan" }) => {
  const glowMap = {
    cyan: "from-cyan-400 via-blue-500 to-indigo-500",
    emerald: "from-emerald-400 via-teal-400 to-green-500",
    rose: "from-rose-500 via-pink-500 to-red-500",
    orange: "from-orange-400 via-amber-400 to-yellow-400",
    purple: "from-purple-500 via-fuchsia-500 to-pink-500",
    blue: "from-blue-400 via-sky-500 to-indigo-500",
    amber: "from-amber-400 via-orange-500 to-yellow-400",
  };

  return (
    <div className="relative group">

      {/* Vibrant Glow Edge */}
      <div
        className={`absolute -inset-[1px] rounded-2xl bg-gradient-to-r ${glowMap[glow]} opacity-70 blur-md group-hover:opacity-100 transition duration-500`}
      />

      {/* Card Body */}
      <div className="relative rounded-2xl p-6 bg-[#0b1220]/80 backdrop-blur-xl border border-white/10 overflow-hidden">

        {/* Shine Sweep */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-700">
          <div className="absolute -left-1/2 top-0 w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent rotate-12 animate-shine" />
        </div>

        <div className="relative z-10 flex justify-between items-start">
          <div>
            <p className="text-sm text-gray-400 mb-2 tracking-wide">
              {label}
            </p>
            <p className="text-4xl font-bold text-white tracking-tight">
              {value}
            </p>
          </div>

          {Icon && (
            <div className="bg-white/5 border border-white/10 p-3 rounded-xl shadow-inner">
              <Icon size={22} className="text-white/90" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
